import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

import SecondaryButton from '../SecondaryButton';
import Table from '../Table';
import AuthorizationPreviewModal from './AuthorizationPreviewModal';

import More from '../../../assets/images/svg/more.svg';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
};

const ListAuthorizationTemplates = () => {
  const getListAuthorizationTemplates = useStoreActions(
    (actions) => actions.middleware.AuthorizationMiddleware.getListAuthorizationTemplates
  );

  const [templates, setTemplates] = useState([]);
  const [onlyActive, setOnlyActive] = useState(false);
  const [search, setSearch] = useState('');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const callList = async () => {
    try {
      const res = await getListAuthorizationTemplates({ activeOnly: onlyActive });
      const items = res?.items || res || [];
      setTemplates(items);
    } catch (e) {
      console.log(e);
      toast.error("❗️ Erreur lors du chargement des autorisations");
    }
  };

  useEffect(() => {
    callList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyActive]);

  // 🔍 filtre + tri
  const filteredTemplates = useMemo(() => {
    const s = search.trim().toLowerCase();
    const list = s
      ? templates.filter(
          (t) =>
            t.title?.toLowerCase().includes(s) ||
            t.bodyText?.toLowerCase().includes(s)
        )
      : templates;

    return [...list].sort((a, b) => {
      const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return db - da;
    });
  }, [templates, search]);

  const openPreview = (tpl) => {
    setPreviewTemplate(tpl);
    setPreviewOpen(true);
  };

  return (
    <section className="md:pt-14 px-4 md:px-9">
      {/* header */}
      <div className="flex flex-col gap-4 border-b border-gray-admin pb-4 pt-11 md:pt-0 mb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="font-bold text-xl">Autorisations d’intervention</h3>
          <p className="text-sm text-gray-500">
            Textes présentés aux locataires pour signature
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Rechercher (titre ou texte)…"
            className="border rounded-lg px-3 py-2 text-sm w-64"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
            />
            Actifs seulement
          </label>

          <SecondaryButton title="+ Ajouter" link={'add'} />
        </div>
      </div>

      {/* table */}
      <div className="mt-6">
        <Table>
          <thead>
            <tr>
              <th>Titre</th>
              <th>Catégorie</th>
              <th>Version</th>
              <th>Actif</th>
              <th>Aperçu</th>
              <th>Dernière maj</th>
              <th className="flex justify-end">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredTemplates.map((t) => (
              <tr key={t.id}>
                <td className="max-w-[360px]">
                  <div className="font-medium">{t.title}</div>
                  <div className="text-xs text-gray-500 line-clamp-1">
                    {t.bodyText}
                  </div>
                </td>

                <td>{t.category || '—'}</td>
                <td>{t.version ?? '—'}</td>

                <td>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      t.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {t.isActive ? 'Oui' : 'Non'}
                  </span>
                </td>

                <td>
                  <button
                    onClick={() => openPreview(t)}
                    className="text-blue-admin underline text-sm"
                  >
                    Voir
                  </button>
                </td>

                <td>{formatDate(t.updatedAt || t.createdAt)}</td>

                <td>
                  <Link
                    to={`edit/${t.id}`}
                    className="float-right w-8 rounded-lg bg-gray-sidebar py-0.5 px-1"
                  >
                    <img src={More} alt="more" />
                  </Link>
                </td>
              </tr>
            ))}

            {!filteredTemplates.length && (
              <tr>
                <td colSpan={7} className="text-center italic text-gray-500 py-6">
                  Aucune autorisation trouvée
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* modal preview */}
      <AuthorizationPreviewModal
        visible={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={previewTemplate?.title}
        bodyText={previewTemplate?.bodyText}
      />
    </section>
  );
};

export default ListAuthorizationTemplates;
